import { useState, useEffect, useCallback, useRef } from 'react';
import { usersService } from '../services/users';
import {
  UserListItem,
  UserDetail,
  UserListQueryParams,
  UserBookingItem,
  UserBookingsQueryParams,
  UserContentItem,
  UserContentQueryParams,
  BookingDetail,
} from '../types/user';

// ============================================================================
// useUserList - Paginated user list with filters
// ============================================================================

interface UseUserListState {
  items: UserListItem[];
  total: number;
  loading: boolean;
  error: string | null;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface UseUserListReturn extends UseUserListState {
  refetch: () => Promise<void>;
}

export function useUserList(params: UserListQueryParams): UseUserListReturn {
  const limit = params.limit || 20;
  const offset = params.offset || 0;
  const currentPage = Math.floor(offset / limit) + 1;

  const [state, setState] = useState<UseUserListState>({
    items: [],
    total: 0,
    loading: true,
    error: null,
    page: currentPage,
    pageSize: limit,
    totalPages: 0,
  });

  const paramsRef = useRef(params);
  paramsRef.current = params;

  const fetchUsers = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await usersService.getUserList(paramsRef.current);
      const { items, total, limit: respLimit, offset: respOffset } = response.data;
      const totalPages = Math.ceil(total / respLimit);
      const page = Math.floor(respOffset / respLimit) + 1;

      setState({
        items,
        total,
        loading: false,
        error: null,
        page,
        pageSize: respLimit,
        totalPages,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch users';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [
    fetchUsers,
    params.search,
    params.isActive,
    params.isVerified,
    params.isProvider,
    params.hasStripeAccount,
    params.createdAfter,
    params.createdBefore,
    params.sortBy,
    params.sortOrder,
    params.offset,
    params.limit,
  ]);

  return {
    ...state,
    refetch: fetchUsers,
  };
}

// ============================================================================
// useUserDetail - Single user detail with aggregated metrics
// ============================================================================

interface UseUserDetailState {
  user: UserDetail | null;
  loading: boolean;
  error: string | null;
}

interface UseUserDetailReturn extends UseUserDetailState {
  refetch: () => Promise<void>;
}

export function useUserDetail(userId: number | null): UseUserDetailReturn {
  const [state, setState] = useState<UseUserDetailState>({
    user: null,
    loading: false,
    error: null,
  });

  const fetchDetail = useCallback(async () => {
    if (!userId) {
      setState({ user: null, loading: false, error: null });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await usersService.getUserDetail(userId);
      setState({ user: response.data, loading: false, error: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch user details';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, [userId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  return {
    ...state,
    refetch: fetchDetail,
  };
}

// ============================================================================
// useUserBookings - Paginated booking history for a user
// ============================================================================

interface UseUserBookingsState {
  items: UserBookingItem[];
  total: number;
  loading: boolean;
  error: string | null;
  hasMore: boolean;
}

interface UseUserBookingsReturn extends UseUserBookingsState {
  refetch: () => Promise<void>;
  loadMore: () => Promise<void>;
}

export function useUserBookings(
  userId: number | null,
  params: UserBookingsQueryParams = {}
): UseUserBookingsReturn {
  const [state, setState] = useState<UseUserBookingsState>({
    items: [],
    total: 0,
    loading: false,
    error: null,
    hasMore: false,
  });

  const paramsRef = useRef(params);
  paramsRef.current = params;

  const fetchBookings = useCallback(async () => {
    if (!userId) {
      setState({ items: [], total: 0, loading: false, error: null, hasMore: false });
      return;
    }
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await usersService.getUserBookings(userId, {
        ...paramsRef.current,
        offset: 0,
      });
      setState({
        items: response.data.items,
        total: response.data.total,
        loading: false,
        error: null,
        hasMore: response.data.hasMore,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch bookings';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, [userId]);

  const loadMore = useCallback(async () => {
    if (!userId || !state.hasMore || state.loading) return;
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const response = await usersService.getUserBookings(userId, {
        ...paramsRef.current,
        offset: state.items.length,
      });
      setState((prev) => ({
        ...prev,
        items: [...prev.items, ...response.data.items],
        hasMore: response.data.hasMore,
        loading: false,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load more bookings';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, [userId, state.hasMore, state.loading, state.items.length]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings, params.role, params.status]);

  return {
    ...state,
    refetch: fetchBookings,
    loadMore,
  };
}

// ============================================================================
// useUserContent - Paginated content for a user
// ============================================================================

interface UseUserContentState {
  items: UserContentItem[];
  total: number;
  loading: boolean;
  error: string | null;
  hasMore: boolean;
}

interface UseUserContentReturn extends UseUserContentState {
  refetch: () => Promise<void>;
  loadMore: () => Promise<void>;
}

export function useUserContent(
  userId: number | null,
  params: UserContentQueryParams = {}
): UseUserContentReturn {
  const [state, setState] = useState<UseUserContentState>({
    items: [],
    total: 0,
    loading: false,
    error: null,
    hasMore: false,
  });

  const paramsRef = useRef(params);
  paramsRef.current = params;

  const fetchContent = useCallback(async () => {
    if (!userId) {
      setState({ items: [], total: 0, loading: false, error: null, hasMore: false });
      return;
    }
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await usersService.getUserContent(userId, {
        ...paramsRef.current,
        offset: 0,
      });
      setState({
        items: response.data.items,
        total: response.data.total,
        loading: false,
        error: null,
        hasMore: response.data.hasMore,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch content';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, [userId]);

  const loadMore = useCallback(async () => {
    if (!userId || !state.hasMore || state.loading) return;
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const response = await usersService.getUserContent(userId, {
        ...paramsRef.current,
        offset: state.items.length,
      });
      setState((prev) => ({
        ...prev,
        items: [...prev.items, ...response.data.items],
        hasMore: response.data.hasMore,
        loading: false,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load more content';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, [userId, state.hasMore, state.loading, state.items.length]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent, params.status]);

  return {
    ...state,
    refetch: fetchContent,
    loadMore,
  };
}

// ============================================================================
// useBookingDetail - Full booking detail
// ============================================================================

interface UseBookingDetailState {
  booking: BookingDetail | null;
  loading: boolean;
  error: string | null;
}

interface UseBookingDetailReturn extends UseBookingDetailState {
  refetch: () => Promise<void>;
}

export function useBookingDetail(bookingId: number | null): UseBookingDetailReturn {
  const [state, setState] = useState<UseBookingDetailState>({
    booking: null,
    loading: false,
    error: null,
  });

  const fetchDetail = useCallback(async () => {
    if (!bookingId) {
      setState({ booking: null, loading: false, error: null });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await usersService.getBookingDetail(bookingId);
      setState({ booking: response.data, loading: false, error: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch booking details';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, [bookingId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  return {
    ...state,
    refetch: fetchDetail,
  };
}
